import type { TokenStore } from "@/domains/tokens/token-store"

import {
  getSqrtRatioAtTick,
  getTickAtSqrtRatio,
} from "../v3-math/tick-math"
import { nearestUsableTick } from "../v3-math/tick-spacing"
import { priceToSqrtRatioX96, sqrtRatioX96ToPrice } from "../v3-math/price-encoding"

/**
 * Convert a human-readable price into a tick, aligned to `tickSpacing`.
 */
export function priceToTick(args: {
  price: string
  token0: TokenStore
  token1: TokenStore
  tickSpacing: number
  invert?: boolean
}): number {
  const sqrtPriceX96 = priceToSqrtRatioX96({
    price: args.price,
    token0: args.token0,
    token1: args.token1,
    invert: args.invert,
  })
  const rawTick = getTickAtSqrtRatio(sqrtPriceX96)
  return nearestUsableTick(rawTick, args.tickSpacing)
}

/**
 * Inverse: given a tick, return the human-readable price string.
 */
export function tickToPriceString(args: {
  tick: number
  token0: TokenStore
  token1: TokenStore
  decimalPlaces?: number
  invert?: boolean
}): string {
  const sqrtPriceX96 = getSqrtRatioAtTick(args.tick)
  return sqrtRatioX96ToPrice({
    sqrtPriceX96,
    token0: args.token0,
    token1: args.token1,
    invert: args.invert,
    decimalPlaces: args.decimalPlaces ?? 6,
  })
}

export function findDeviation(args: {
  price: string
  currentPrice: string
}): { percent: number; direction: "above" | "below" } {
  const p = parseFloat(args.price)
  const c = parseFloat(args.currentPrice)
  if (!Number.isFinite(p) || !Number.isFinite(c) || c === 0) {
    return { percent: 0, direction: "above" }
  }
  const pct = ((p - c) / c) * 100
  return { percent: Math.abs(pct), direction: pct >= 0 ? "above" : "below" }
}
